// Column filters: enumerable (with an optional refinement), numeric and date. Values, drafts, validation
// and matching are pure so the filter editors and the page-backed request share them.
//
// Committed value shapes, by filter type:
//   enum     string[]  or, while the refinement is on, {selected: string[], refined: true}
//   numeric  {mode: 'equal'|'greaterThan'|'lessThan'|'between', from: ?number, to: ?number}
//   date     {mode: 'on'|'before'|'after'|'between', from: ?string, to: ?string}  ('YYYY-MM-DD')

import { cellValue, isNumber, parseIsoDate, productLocale } from '../format'
import { isMissing } from './sort'

export const NUMERIC_MODES = ['equal', 'greaterThan', 'lessThan', 'between']
export const DATE_MODES = ['on', 'before', 'after', 'between']
export const DEFAULT_NUMERIC_MODE = 'equal'
export const DEFAULT_DATE_MODE = 'on'

// earlier names for the exclusive numeric comparisons
const MODE_ALIASES = { atLeast: 'greaterThan', atMost: 'lessThan' }

export const FILTER_ERRORS = {
  number: 'Enter a valid number',
  date: 'Enter a valid date',
  range: 'Start must be before or equal to end',
}

// The filter a column offers, from its normalized capabilities or its raw `filterable` declaration.
export const filterTypeOf = column => {
  if (!column) return null
  if (column.capabilities) return column.capabilities.filter || null
  if (!column.filterable) return null
  if (column.filterable !== true) return column.filterable
  if (column.type === 'numeric') return 'numeric'
  if (column.type === 'date') return 'date'
  return 'enum'
}

const shapeOf = value => {
  if (Array.isArray(value)) return 'enum'
  if (value && typeof value === 'object') {
    if (Array.isArray(value.selected)) return 'enum'
    if ('mode' in value) return 'range'
  }
  return null
}

// ---- enumerable ----

export const enumSelection = value => {
  if (Array.isArray(value)) return value
  if (value && Array.isArray(value.selected)) return value.selected
  return []
}

export const refinementOn = value =>
  Boolean(value) && !Array.isArray(value) && value.refined === true

// The refinement only lives while its refined option is selected.
export const enumValue = (column, selected, refined = false) => {
  const option = column && column.refinement && column.refinement.option
  return refined && option && selected.includes(option)
    ? { selected, refined: true }
    : selected
}

// Declared options stay stable; otherwise options come from the complete client-backed source.
export const enumOptions = (column, rows, locale) => {
  if (column.options) return column.options
  const values = new Set()
  rows.forEach(row => {
    const value = cellValue(column, row)
    if (!isMissing(value)) values.add(String(value))
  })
  const collator = new Intl.Collator(productLocale(locale), {
    sensitivity: 'base',
    numeric: true,
  })
  return Array.from(values).sort(collator.compare)
}

// ---- numeric and date drafts ----

export const filterMode = (type, mode) => {
  const modes = type === 'date' ? DATE_MODES : NUMERIC_MODES
  const named = MODE_ALIASES[mode] || mode
  if (modes.includes(named)) return named
  return type === 'date' ? DEFAULT_DATE_MODE : DEFAULT_NUMERIC_MODE
}

const NUMBER = /^[-+]?(\d+(\.\d*)?|\.\d+)(e[-+]?\d+)?$/i

// null for an empty bound, NaN for one that is not a number
export const parseNumber = input => {
  if (input === null || input === undefined) return null
  if (typeof input === 'number') return Number.isFinite(input) ? input : NaN
  const text = String(input).trim()
  if (!text) return null
  return NUMBER.test(text) ? Number(text) : NaN
}

// null for an empty bound, undefined for one that is not a real calendar date
const parseDateBound = input => {
  if (input === null || input === undefined) return null
  const text = String(input).trim()
  if (!text) return null
  return parseIsoDate(text) === null ? undefined : text
}

/**
 * Checks a numeric or date draft. A valid draft yields the value to commit; an invalid one yields its
 * inline error and must not commit, so the last valid committed filter stays in effect.
 * @param {'numeric'|'date'} type
 * @param {{mode?: string, from?: *, to?: *}} draft
 * @returns {{value: ?object, error: ?string}}
 */
export const checkRangeDraft = (type, draft) => {
  const mode = filterMode(type, draft && draft.mode)
  const between = mode === 'between'
  const parse = type === 'date' ? parseDateBound : parseNumber
  const invalid = type === 'date' ? v => v === undefined : Number.isNaN
  const from = parse(draft && draft.from)
  const to = between ? parse(draft && draft.to) : null
  if (invalid(from) || invalid(to))
    return {
      value: null,
      error: type === 'date' ? FILTER_ERRORS.date : FILTER_ERRORS.number,
    }
  if (between && from !== null && to !== null) {
    const reversed =
      type === 'date' ? parseIsoDate(from) > parseIsoDate(to) : from > to
    if (reversed) return { value: null, error: FILTER_ERRORS.range }
  }
  return { value: { mode, from, to }, error: null }
}

// A mode change keeps the first bound and drops the second when leaving Between.
export const changeRangeMode = (type, draft, mode) => {
  const next = filterMode(type, mode)
  return {
    ...(draft || {}),
    mode: next,
    to: next === 'between' ? (draft && draft.to) ?? null : null,
  }
}

// ---- activity ----

export const isFilterActive = value => {
  const shape = shapeOf(value)
  if (shape === 'enum') return enumSelection(value).length > 0
  if (shape === 'range')
    return (
      (value.from !== null && value.from !== undefined) ||
      (value.mode === 'between' && value.to !== null && value.to !== undefined)
    )
  return false
}

export const activeFilters = filters =>
  Object.entries(filters || {}).filter(([, value]) => isFilterActive(value))

// ---- matching ----

const compare = (mode, value, from, to) => {
  if (mode === 'between')
    return (from === null || value >= from) && (to === null || value <= to)
  if (from === null) return true
  if (mode === 'greaterThan' || mode === 'after') return value > from
  if (mode === 'lessThan' || mode === 'before') return value < from
  return value === from
}

const matchesEnum = (column, value, row) => {
  const selected = enumSelection(value)
  const raw = cellValue(column, row)
  if (isMissing(raw)) return false
  const text = String(raw)
  const { refinement } = column
  // with the refinement on, only the refined option matches, and only rows that meet its condition
  if (refinementOn(value) && refinement && selected.includes(refinement.option))
    return text === refinement.option && Boolean(refinement.test(row))
  return selected.includes(text)
}

const matchesNumeric = (column, value, row) => {
  const raw = cellValue(column, row)
  if (!isNumber(raw)) return false
  const from = isNumber(value.from) ? value.from : null
  const to = isNumber(value.to) ? value.to : null
  return compare(filterMode('numeric', value.mode), raw, from, to)
}

const matchesDate = (column, value, row) => {
  const time = parseIsoDate(cellValue(column, row))
  if (time === null) return false
  return compare(
    filterMode('date', value.mode),
    time,
    parseIsoDate(value.from),
    parseIsoDate(value.to)
  )
}

export const matchesFilter = (column, value, row) => {
  if (!isFilterActive(value)) return true
  const type = filterTypeOf(column) || (shapeOf(value) === 'enum' && 'enum')
  if (type === 'numeric') return matchesNumeric(column, value, row)
  if (type === 'date') return matchesDate(column, value, row)
  return matchesEnum(column, value, row)
}

// OR within a column, AND across columns; an inactive value is ignored, and so is a filter whose column is
// gone or hidden.
export const matchesFilters = (row, columns, filters, hidden = []) =>
  activeFilters(filters).every(([key, value]) => {
    const column = columns.find(c => c.key === key)
    if (!column || hidden.includes(key)) return true
    return matchesFilter(column, value, row)
  })

// Drops inactive values and filters on columns that are gone or hidden, so state stays minimal.
export const pruneFilters = (filters, { keys, hidden = [] } = {}) => {
  const next = {}
  activeFilters(filters).forEach(([key, value]) => {
    if (keys && !keys.includes(key)) return
    if (hidden.includes(key)) return
    next[key] = value
  })
  return next
}
